import React, { useState, useEffect } from 'react';
import config from '../config';
import { useAuth } from '../context/AuthContext';

/**
 * ChunkImage component - lazily loads images from source document chunks.
 * Fetches SAS URLs on-demand from the backend when the image enters the viewport.
 */
const ChunkImage = ({ imageId, source, alt }) => {
  const [imageUrl, setImageUrl] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { getAuthHeaders } = useAuth();

  useEffect(() => {
    if (!imageId || !source) return;

    const fetchImageUrl = async () => {
      setLoading(true);
      setError(null);

      try {
        const authHeaders = getAuthHeaders();
        const params = new URLSearchParams({
          source: source,
          image_id: imageId
        });

        const response = await fetch(
          `${config.API_BASE_URL}/chunk/image?${params}`,
          { headers: authHeaders }
        );

        if (!response.ok) {
          throw new Error('Failed to load image');
        }

        const data = await response.json();
        setImageUrl(data.url);
      } catch (err) {
        console.error('Error fetching chunk image:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    fetchImageUrl();
  }, [imageId, source, getAuthHeaders]);

  if (loading) {
    return (
      <div className="chunk-image-placeholder">
        <div className="chunk-image-loading">Loading image...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="chunk-image-placeholder">
        <div className="chunk-image-error" title={error}>
          [Image unavailable]
        </div>
      </div>
    );
  }

  if (!imageUrl) {
    return null;
  }

  return (
    <div className="chunk-image-container">
      <img
        src={imageUrl}
        alt={alt || 'Document image'}
        className="chunk-image"
        loading="lazy"
      />
    </div>
  );
};

export default ChunkImage;
